/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { AmplitudeService } from '@inyova/inyova-shared';
import { cloneDeep } from 'lodash-es';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromStrategy from '@strategy/reducers';

import { COMPANY_SECTOR_API_KEY, MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { Company } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-company-detail',
  templateUrl: './strategy-company-detail.component.html',
  styleUrls: ['../strategy-personalise/strategy-personalise.component.scss', './strategy-company-detail.component.scss'],
  standalone: false
})
export class StrategyCompanyDetailComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  @Input() showActions: boolean;
  @Input() appLocation: 'ch' | 'de';
  @Input() loadingID: number;
  @Input() blockedCompaniesIDs = [];
  @Input() listType = '';
  @Input() selectedCompanyID = 0;
  @Input() wishedCompaniesIDs = [];
  @Input() view: string;
  @Input() activeTab: string;

  @Output() showWhyInyova = new EventEmitter<boolean>();
  @Output() updateLoadingID = new EventEmitter<number>();

  portfolioCompanyListIDs: { wishedCompanyIDs: number[]; blockedCompanyIDs: number[] } = null;

  isLoading = true;
  company: Company;
  limitDesc = true;
  limitExtraDesc = true;
  limitGoodDesc = true;
  limitBadDesc = true;
  limitSize = [200, 120];

  constructor(
    private store: Store,
    private trackingService: TrackingService,
    private amplitudeService: AmplitudeService
  ) {
    this.store.pipe(select(fromStrategy.selectCompanyDetail), takeUntil(this.onDestroy$)).subscribe((companyDetail) => {
      this.company = cloneDeep(companyDetail.company);
      this.isLoading = companyDetail.loading;
      this.limitSize = window.innerWidth < MOBILE_VIEW_TRESHOLD ? [200, 120] : [400, 320];
    });

    this.store.pipe(select(fromStrategy.selectPortfolioCompanyListIDs), takeUntil(this.onDestroy$)).subscribe((companyIDs) => {
      this.portfolioCompanyListIDs = companyIDs;
      // update company detail icons when companyIDs is up to date.
      this.company.is_wishlist = companyIDs.wishedCompanyIDs.includes(this.company.id);
      this.company.is_blacklist = companyIDs.blockedCompanyIDs.includes(this.company.id);
    });
  }

  getCompanySector(company: Company): string {
    if (!COMPANY_SECTOR_API_KEY[company.sector]) return '';
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `companyDescription.section1.sector${COMPANY_SECTOR_API_KEY[company.sector]}`;
  }

  getCompanyCoverImage(company: Company): { backgroundImage: string } {
    return {
      backgroundImage: company.img ? `url('${company.img}')` : ''
    };
  }

  updateLoadingCompany(event: number): void {
    this.updateLoadingID.emit(event);
  }

  openWhyInyova($event: Event): void {
    $event.stopPropagation();
    this.showWhyInyova.emit(true);
  }

  limitCopy(key: string): void {
    let activity = '';
    let more;
    switch (key) {
      case 'limitDesc':
        this.limitDesc = !this.limitDesc;
        activity = 'Company snapshot';
        more = this.limitDesc;
        break;
      case 'limitExtraDesc':
        this.limitExtraDesc = !this.limitExtraDesc;
        activity = 'Extra';
        more = this.limitExtraDesc;
        break;
      case 'limitGoodDesc':
        this.limitGoodDesc = !this.limitGoodDesc;
        activity = 'Impact & recognition';
        more = this.limitGoodDesc;
        break;
      case 'limitBadDesc':
        this.limitBadDesc = !this.limitBadDesc;
        activity = 'Controversies & critique';
        more = this.limitBadDesc;
        break;
      default:
        break;
    }
    this.trackingService.trackActivity(`[Button] ${activity} - ${more ? 'show less' : 'read more'}`);
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'Strategy company detail',
      consultation_method: 'hover'
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
